import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Globe, User, Mail, Phone, Building2, MapPin, Briefcase, 
  Linkedin, Twitter, Eye, Zap, Settings2, List, Link2,
  CheckCircle, AlertCircle, Loader2
} from 'lucide-react';
import { useLeadHarvest, HarvestProgress, HarvestedContact } from '@/hooks/useLeadHarvest';
import { LeadDetailSheet } from './LeadDetailSheet';

export function AILeadHarvest() {
  const [companyUrl, setCompanyUrl] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [bulkUrls, setBulkUrls] = useState('');
  const [sourceType, setSourceType] = useState<'website' | 'linkedin' | 'twitter' | 'facebook'>('website');
  const [enrichWithLinkedIn, setEnrichWithLinkedIn] = useState(true);
  const [scrapeDepth, setScrapeDepth] = useState<'shallow' | 'deep'>('shallow');
  const [selectedLead, setSelectedLead] = useState<any>(null);
  const [detailSheetOpen, setDetailSheetOpen] = useState(false);
  
  const { 
    isHarvesting, 
    harvestedContacts, 
    companyInfo, 
    progress,
    harvestContacts, 
    harvestBulk 
  } = useLeadHarvest();

  const handleHarvest = async () => {
    if (!companyUrl.trim()) return;
    await harvestContacts(companyUrl.trim(), companyName.trim() || undefined, {
      sourceType,
      enrichWithLinkedIn,
      scrapeDepth
    });
  };

  const handleBulkHarvest = async () => {
    const urls = bulkUrls.split('\n').map(u => u.trim()).filter(Boolean);
    if (urls.length === 0) return;
    await harvestBulk(urls, { enrichWithLinkedIn, scrapeDepth });
  };

  const handleViewLead = (contact: HarvestedContact) => {
    setSelectedLead(contact);
    setDetailSheetOpen(true);
  };

  const getSourceIcon = () => {
    switch (sourceType) {
      case 'linkedin': return <Linkedin className="h-4 w-4" />;
      case 'twitter': return <Twitter className="h-4 w-4" />;
      default: return <Globe className="h-4 w-4" />;
    }
  };

  const getPlaceholder = () => {
    switch (sourceType) {
      case 'linkedin': return 'https://linkedin.com/company/acme';
      case 'twitter': return 'https://twitter.com/acme';
      case 'facebook': return 'https://facebook.com/acme';
      default: return 'https://company.com';
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 70) return 'text-green-400';
    if (score >= 40) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getSeniorityBadge = (seniority: string) => {
    const colors: Record<string, string> = {
      'c-level': 'bg-purple-500/20 text-purple-400',
      'executive': 'bg-blue-500/20 text-blue-400',
      'senior': 'bg-green-500/20 text-green-400',
      'mid': 'bg-yellow-500/20 text-yellow-400',
      'entry': 'bg-muted text-muted-foreground',
    };
    return colors[seniority] || colors.entry;
  };

  return (
    <>
      <Card className="border-primary/20 bg-gradient-to-br from-card to-card/80">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5 text-primary" />
            AI Lead Harvesting
          </CardTitle>
          <CardDescription>
            Scrape websites & social media to automatically find contacts with LinkedIn enrichment
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Tabs defaultValue="single" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="single" className="gap-2">
                <Link2 className="h-4 w-4" />
                Single URL
              </TabsTrigger>
              <TabsTrigger value="bulk" className="gap-2">
                <List className="h-4 w-4" />
                Bulk Import
              </TabsTrigger>
            </TabsList>

            <TabsContent value="single" className="space-y-4 mt-4">
              <div className="grid gap-4 sm:grid-cols-3">
                <div className="space-y-2">
                  <Label>Source Type</Label>
                  <Select value={sourceType} onValueChange={(v: any) => setSourceType(v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="website"><div className="flex items-center gap-2"><Globe className="h-4 w-4" /> Website</div></SelectItem>
                      <SelectItem value="linkedin"><div className="flex items-center gap-2"><Linkedin className="h-4 w-4" /> LinkedIn</div></SelectItem>
                      <SelectItem value="twitter"><div className="flex items-center gap-2"><Twitter className="h-4 w-4" /> Twitter/X</div></SelectItem>
                      <SelectItem value="facebook"><div className="flex items-center gap-2"><Globe className="h-4 w-4" /> Facebook</div></SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="company-url">URL *</Label>
                  <Input
                    id="company-url"
                    placeholder={getPlaceholder()}
                    value={companyUrl}
                    onChange={(e) => setCompanyUrl(e.target.value)}
                    disabled={isHarvesting}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="company-name">Company Name (optional)</Label>
                  <Input
                    id="company-name"
                    placeholder="Acme Corp"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    disabled={isHarvesting}
                  />
                </div>
              </div>

              {/* Advanced Options */}
              <div className="flex flex-wrap items-center gap-6 p-3 rounded-lg bg-muted/50">
                <div className="flex items-center gap-2">
                  <Settings2 className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Options:</span>
                </div>
                <div className="flex items-center gap-2">
                  <Switch 
                    id="linkedin-enrich" 
                    checked={enrichWithLinkedIn} 
                    onCheckedChange={setEnrichWithLinkedIn}
                    disabled={isHarvesting || sourceType === 'linkedin'}
                  />
                  <Label htmlFor="linkedin-enrich" className="text-sm cursor-pointer">
                    <span className="flex items-center gap-1">
                      <Linkedin className="h-3 w-3" /> Enrich with LinkedIn
                    </span>
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-sm">Depth:</Label>
                  <Select value={scrapeDepth} onValueChange={(v: any) => setScrapeDepth(v)} disabled={isHarvesting}>
                    <SelectTrigger className="w-28 h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="shallow">Shallow</SelectItem>
                      <SelectItem value="deep">Deep</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Button 
                onClick={handleHarvest} 
                disabled={isHarvesting || !companyUrl.trim()}
                className="w-full gap-2"
              >
                {isHarvesting ? <Loader2 className="h-4 w-4 animate-spin" /> : getSourceIcon()}
                {isHarvesting ? 'Harvesting...' : `Harvest from ${sourceType === 'website' ? 'Website' : sourceType.charAt(0).toUpperCase() + sourceType.slice(1)}`}
              </Button>
            </TabsContent>

            <TabsContent value="bulk" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="bulk-urls">URLs (one per line)</Label>
                <Textarea
                  id="bulk-urls"
                  placeholder={`https://company1.com\nhttps://company2.com\nhttps://linkedin.com/company/company3`}
                  value={bulkUrls}
                  onChange={(e) => setBulkUrls(e.target.value)}
                  disabled={isHarvesting}
                  rows={5}
                />
                <p className="text-xs text-muted-foreground">
                  Enter up to 10 URLs. Each will be scraped sequentially.
                </p>
              </div>

              <div className="flex items-center gap-4 p-3 rounded-lg bg-muted/50">
                <div className="flex items-center gap-2">
                  <Switch 
                    id="bulk-linkedin-enrich" 
                    checked={enrichWithLinkedIn} 
                    onCheckedChange={setEnrichWithLinkedIn}
                    disabled={isHarvesting}
                  />
                  <Label htmlFor="bulk-linkedin-enrich" className="text-sm cursor-pointer">
                    LinkedIn Enrichment
                  </Label>
                </div>
              </div>

              <Button 
                onClick={handleBulkHarvest} 
                disabled={isHarvesting || !bulkUrls.trim()}
                className="w-full gap-2"
              >
                {isHarvesting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
                {isHarvesting ? 'Processing...' : 'Harvest All URLs'}
              </Button>
            </TabsContent>
          </Tabs>

          {/* Progress Indicator */}
          {isHarvesting && progress && (
            <div className="space-y-2 p-3 rounded-lg bg-muted/30">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{progress.stage}</span>
                <span className="font-medium">{progress.percent}%</span>
              </div>
              <Progress value={progress.percent} className="h-2" />
              {progress.currentUrl && (
                <p className="text-xs text-muted-foreground truncate">
                  {progress.currentUrl}
                </p>
              )}
            </div>
          )}

          {/* Company Info */}
          {companyInfo && (
            <Card className="bg-muted/50 border-primary/10">
              <CardContent className="pt-4">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-primary" />
                  Company Information
                </h4>
                <div className="grid gap-2 sm:grid-cols-2 text-sm">
                  {companyInfo.name && (
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground">Name:</span>
                      <span className="font-medium">{companyInfo.name}</span>
                    </div>
                  )}
                  {companyInfo.industry && (
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground">Industry:</span>
                      <Badge variant="outline">{companyInfo.industry}</Badge>
                    </div>
                  )}
                  {companyInfo.location && (
                    <div className="flex items-center gap-2">
                      <MapPin className="h-3 w-3 text-muted-foreground" />
                      <span>{companyInfo.location}</span>
                    </div>
                  )}
                  {companyInfo.employee_count && (
                    <div className="flex items-center gap-2">
                      <User className="h-3 w-3 text-muted-foreground" />
                      <span>{companyInfo.employee_count} employees</span>
                    </div>
                  )}
                  {companyInfo.general_email && (
                    <div className="flex items-center gap-2">
                      <Mail className="h-3 w-3 text-muted-foreground" />
                      <span>{companyInfo.general_email}</span>
                    </div>
                  )}
                  {companyInfo.general_phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="h-3 w-3 text-muted-foreground" />
                      <span>{companyInfo.general_phone}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Harvested Contacts */}
          {harvestedContacts.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Harvested Contacts
                  </span>
                  <Badge variant="secondary">{harvestedContacts.length} found</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {harvestedContacts.map((contact) => (
                    <div
                      key={contact.id}
                      className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors cursor-pointer group"
                      onClick={() => handleViewLead(contact)}
                    >
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div className="h-10 w-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                          <User className="h-5 w-5 text-primary" />
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium flex items-center gap-2 flex-wrap">
                            <span className="truncate">{contact.first_name} {contact.last_name}</span>
                            {contact.metadata?.seniority_level && (
                              <Badge className={`text-xs ${getSeniorityBadge(contact.metadata.seniority_level)}`}>
                                {contact.metadata.seniority_level}
                              </Badge>
                            )}
                          </div>
                          <div className="text-sm text-muted-foreground truncate">
                            {contact.metadata?.job_title || contact.company_name || 'No title'}
                          </div>
                          <div className="flex items-center gap-3 mt-1">
                            {contact.email && (
                              <span className="text-xs text-muted-foreground flex items-center gap-1">
                                <Mail className="h-3 w-3" />
                                <CheckCircle className="h-3 w-3 text-green-400" />
                              </span>
                            )}
                            {contact.phone && (
                              <span className="text-xs text-muted-foreground flex items-center gap-1">
                                <Phone className="h-3 w-3" />
                                <CheckCircle className="h-3 w-3 text-green-400" />
                              </span>
                            )}
                            {contact.metadata?.linkedin_url && (
                              <span className="text-xs text-muted-foreground flex items-center gap-1">
                                <Linkedin className="h-3 w-3" />
                                <CheckCircle className="h-3 w-3 text-green-400" />
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {contact.lead_score !== null && (
                          <div className="text-right">
                            <div className={`text-lg font-bold ${getScoreColor(contact.lead_score)}`}>
                              {contact.lead_score}
                            </div>
                            <div className="text-xs text-muted-foreground">Score</div>
                          </div>
                        )}
                        <Button 
                          variant="ghost" 
                          size="icon"
                          className="opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewLead(contact);
                          }}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </CardContent>
      </Card>

      <LeadDetailSheet 
        lead={selectedLead}
        open={detailSheetOpen}
        onOpenChange={setDetailSheetOpen}
      />
    </>
  );
}
